import { useState, useEffect, useRef } from "react";
import { Bot, Send, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import DOMPurify from "dompurify";

const AIChatPage = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `crm_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Welcome message
    setMessages([{ role: "assistant", text: "مرحباً! أنا المساعد الذكي للعيادة. اسألني عن أي شي يخص المرضى، الجلسات، المالية، أو أي بيانات بالنظام." }]);
    inputRef.current?.focus();
  }, []);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);

    try {
      const res = await api.post("/ai/crm-chat", { message: msg, session_id: sessionId }, token);
      setMessages(prev => [...prev, { role: "assistant", text: res.data.response }]);
    } catch (err) {
      console.error("AI Chat error:", err);
      toast.error("خطأ في المساعد الذكي");
      setMessages(prev => [...prev, { role: "assistant", text: "عذراً، حدث خطأ. حاول مرة أخرى." }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", text: "تم مسح المحادثة. كيف أكدر أساعدك؟" }]);
  };

  const formatMessage = (text) => {
    const html = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
    return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
  };

  const quickQuestions = [
    "كم مريض عدنا بالمجموع؟",
    "شنو الحالة المالية العامة؟",
    "مين المرضى اللي عليهم باقي؟",
    "كم جلسة سجلنا؟",
  ];

  return (
    <div className="animate-fade-in h-[calc(100vh-80px)] flex flex-col" data-testid="ai-chat-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            المساعد الذكي
          </h1>
          <p className="text-gray-500 text-sm mt-1">اسأل أي سؤال عن بيانات العيادة</p>
        </div>
        <button onClick={clearChat} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all" data-testid="clear-chat-btn">
          <Trash2 className="w-4 h-4" /> مسح المحادثة
        </button>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={`msg-${i}`} className={`flex ${msg.role === "user" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === "user" 
                  ? "bg-purple-600 text-white rounded-br-sm" 
                  : "bg-gray-100 text-gray-800 rounded-bl-sm"
              }`} data-testid={`chat-msg-${i}`}>
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-[10px] font-bold text-purple-500">المساعد الذكي</span>
                  </div>
                )}
                <div className="text-sm leading-relaxed">{formatMessage(msg.text)}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-end">
              <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                <span className="text-sm text-gray-500">يفكر...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions - show only when no user messages */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-gray-400 mb-2">أسئلة سريعة:</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q, i) => (
                <button
                  key={`quick-${i}`}
                  onClick={() => sendMessage(q)}
                  className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-xl text-xs font-medium hover:bg-purple-100 transition-colors border border-purple-100"
                  data-testid={`quick-q-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-gray-100 p-3">
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="اكتب سؤالك هنا..."
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:outline-none text-sm"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="ai-chat-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
